import axios from 'axios';
import { useEffect, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'react-toastify';
import Loading from '../../../components/Loading/Loading';
import Main from '../../../layouts/Main/Main';

import { FaBoxArchive, FaRegTrashCan } from 'react-icons/fa6';
import Global from '../../../components/Expense/Details/Cards/Global/Global';
import Infos from '../../../components/Expense/Details/Cards/Infos/Infos';
import Lines from '../../../components/Expense/Details/Cards/Lines/Lines';
import { IBreadcrumbProps } from '../../../components/Header/Header';
import { IconWrapper } from '../../../components/IconWrapper/IconWrapper';
import { IExpense } from '../../../types';
import styles from './Details.module.scss';
import moment from 'moment';
import 'moment/locale/fr';
import { FaRoute } from 'react-icons/fa6';

const Details = () => {
  const params = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(true);
  const [expense, setExpense] = useState<IExpense | undefined>(undefined);
  const [breadcrumb, setBreadcrumb] = useState<IBreadcrumbProps[]>([]);

  useEffect(() => {
    if (expense) {
      setBreadcrumb([
        { name: 'Home', path: '/' },
        { name: 'Expense Reports', path: '/expense' },
        { name: expense.name, path: `/expense/${params.id}`, active: true },
      ]);
    }
  }, [expense, params.id]);

  const getExpenseDetails = () => {
    axios
      .get(`/api/expense/${params.id}`)
      .then((response) => {
        if (response.status === 200) {
          setExpense(response.data);
        }
      })
      .catch((err) => {
        toast.error(err.response.data);
      })
      .finally(() => {
        setTimeout(() => setLoading(false), 250);
      });
  };

  const handleDeleteExpense = () => {
    axios.delete(`/api/expense/${params.id}`).then(() => {
      navigate('/expense');
    });
  };

  const handleArchive = () => {
    if (!expense || !window.confirm(expense.active ? 'Archive this expense report?' : 'Restore this expense report?')) return;
    axios
      .patch(`/api/expense/${expense._id}/archive`)
      .then(getExpenseDetails)
      .catch((error) => toast.error(error.response?.data ?? 'Unable to update archive status'));
  };

  const getTotalRepaid = (currentExpense: IExpense) => {
    const distance = currentExpense.lines.reduce((sum, line) => sum + line.distance, 0);
    if (distance < 5000) return distance * 0.665;
    if (distance < 20000) return distance * 0.374 + 1457;
    return distance * 0.447;
  };

  const handleOnChange = () => {
    getExpenseDetails();
  };

  useEffect(() => {
    setLoading(true);
    getExpenseDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const actions = (
    <>
      {expense && (
        <Button variant="outline-secondary" size="sm" onClick={handleArchive}>
          <IconWrapper icon={FaBoxArchive} /> {expense.active ? 'Archive' : 'Restore'}
        </Button>
      )}{' '}
      <Button variant="outline-danger" size="sm" onClick={handleDeleteExpense}>
        <IconWrapper icon={FaRegTrashCan} /> Delete
      </Button>
    </>
  );

  return (
    <Main breadcrumb={breadcrumb} actions={actions}>
      <Container className="mt-3">
        <Loading isLoading={loading}>
          <>
            {!expense && <Alert variant="danger">Expense not found</Alert>}
            {expense && (
              <div className={styles.Page}>
                <section className={styles.Hero}>
                  <Row className="align-items-center">
                    <Col md={8} className={styles.HeroContent}>
                      <div className={styles.Eyebrow}>{expense.type === 'trip' ? 'Trip report' : 'Expense report'}</div>
                      <h1 className={styles.Title}>{expense.name}</h1>
                      <p className={styles.Period}>
                        {expense.type === 'trip' && expense.start_date && expense.end_date
                          ? `${moment(expense.start_date).format('LL')} - ${moment(expense.end_date).format('LL')}`
                          : moment(expense.date).format('LL')}
                      </p>
                    </Col>
                    <Col md={4} className={styles.Metrics}>
                      <div className={styles.Metric}><strong>{expense.lines.length}</strong><span>Lines</span></div>
                      <div className={styles.Metric}><strong>{expense.lines.reduce((sum, line) => sum + line.distance, 0)}</strong><span>Km</span></div>
                      <div className={styles.Metric}><strong>{getTotalRepaid(expense).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}</strong><span>Total</span></div>
                    </Col>
                  </Row>
                </section>
                <Row className="mb-4">
                  <Col sm={4}>
                    <Global expense={expense} onChange={handleOnChange} />
                  </Col>
                  <Col sm={4}>
                    <div className={styles.TypePanel}>
                      <IconWrapper icon={FaRoute} />
                      <span>Report type</span>
                      <strong>{expense.type === 'trip' ? 'Mileage trip' : 'Standard expense'}</strong>
                    </div>
                  </Col>
                  <Col sm={4}>
                    <Infos expense={expense} />
                  </Col>
                </Row>
                <Row>
                  <Col sm={12}>
                    <Lines expense={expense} onChange={handleOnChange} />
                  </Col>
                </Row>
              </div>
            )}
          </>
        </Loading>
      </Container>
    </Main>
  );
};

export default Details;
